import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { User, Mail, Save, Loader2, CreditCard, ExternalLink, CheckCircle, AlertCircle, RefreshCw, DollarSign, Clock, XCircle, Camera, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navigation from "../components/Navigation";
import UserAvatar from "../components/UserAvatar";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfilePage = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const applyPicture = (picture) => {
    const updated = { ...user, picture };
    localStorage.setItem("user", JSON.stringify(updated));
    setUser(updated);
  };

  const resizeImage = (file, max = 512) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("resize failed"))),
          "image/jpeg",
          0.85,
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploadingPhoto(true);
    try {
      const blob = await resizeImage(file, 512);
      const fd = new FormData();
      fd.append("file", blob, "avatar.jpg");
      const token = localStorage.getItem("access_token");
      const resp = await axios.post(`${API}/auth/profile-picture`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyPicture(resp.data.picture);
      toast.success("Profile photo updated");
    } catch (err) {
      console.error("Photo upload error:", err);
      toast.error(err.response?.data?.detail || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoRemove = async () => {
    try {
      const token = localStorage.getItem("access_token");
      await axios.delete(`${API}/auth/profile-picture`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      applyPicture(null);
      toast.success("Profile photo removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove photo");
    }
  };

  // Stripe Connect state
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState(null);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchStripeStatus();
      fetchPayoutHistory();
    }
  }, [user]);

  // Check for Stripe onboarding return
  useEffect(() => {
    const stripeReturn = searchParams.get("stripe_return");
    if (stripeReturn === "success") {
      toast.success("Stripe account setup complete!");
      fetchStripeStatus();
      // Clear the param
      navigate("/profile", { replace: true });
    } else if (stripeReturn === "refresh") {
      toast.info("Please complete your Stripe account setup");
      fetchStripeStatus();
      navigate("/profile", { replace: true });
    }
  }, [searchParams, navigate]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setName(response.data.name || "");
    } catch (error) {
      console.error("Fetch profile error:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchStripeStatus = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/connect/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStripeStatus(response.data);
      
      // Show toast if payouts were processed
      if (response.data.processed_payouts?.length > 0) {
        const total = response.data.processed_payouts.reduce((sum, p) => sum + p.amount, 0);
        toast.success(`${response.data.processed_payouts.length} pending payout(s) processed! Total: $${total.toFixed(2)}`);
        fetchPayoutHistory(); // Refresh history
      }
    } catch (error) {
      console.error("Fetch Stripe status error:", error);
      setStripeStatus({ status: "error", message: "Could not fetch Stripe status" });
    }
  };

  const fetchPayoutHistory = async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API}/connect/payouts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPayoutHistory(response.data);
    } catch (error) {
      console.error("Fetch payout history error:", error);
    }
  };

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const currentUrl = window.location.origin;

      const response = await axios.post(
        `${API}/connect/create-account`,
        {
          return_url: `${currentUrl}/profile?stripe_return=success`,
          refresh_url: `${currentUrl}/profile?stripe_return=refresh`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      } else if (response.data.status === "already_connected") {
        toast.success("Your Stripe account is already connected!");
        fetchStripeStatus();
      }
    } catch (error) {
      console.error("Connect Stripe error:", error);
      toast.error(error.response?.data?.detail || "Failed to start Stripe setup");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRefreshOnboarding = async () => {
    setStripeLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const currentUrl = window.location.origin;

      const response = await axios.post(
        `${API}/connect/refresh-onboarding`,
        {
          return_url: `${currentUrl}/profile?stripe_return=success`,
          refresh_url: `${currentUrl}/profile?stripe_return=refresh`,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.onboarding_url) {
        window.location.href = response.data.onboarding_url;
      }
    } catch (error) {
      console.error("Refresh onboarding error:", error);
      toast.error(error.response?.data?.detail || "Failed to refresh onboarding");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    setStripeLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API}/connect/create-login-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.dashboard_url) {
        window.open(response.data.dashboard_url, "_blank");
      }
    } catch (error) {
      console.error("Dashboard link error:", error);
      toast.error(error.response?.data?.detail || "Failed to open Stripe dashboard");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleRetryFailedPayouts = async () => {
    setStripeLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.post(
        `${API}/connect/retry-failed-payouts`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const successful = response.data.processed?.filter(p => p.status === "completed") || [];
      if (successful.length > 0) {
        const total = successful.reduce((sum, p) => sum + p.amount, 0);
        toast.success(`${successful.length} payout(s) recovered! Total: $${total.toFixed(2)}`);
      } else {
        toast.info(response.data.message || "No payouts to retry");
      }
      
      fetchStripeStatus();
      fetchPayoutHistory();
    } catch (error) {
      console.error("Retry payouts error:", error);
      toast.error(error.response?.data?.detail || "Failed to retry payouts");
    } finally {
      setStripeLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!name.trim() || name.trim().length < 2) {
      toast.error("Please enter your name (at least 2 characters)");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.patch(
        `${API}/auth/profile`,
        { name: name.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const updatedUser = { ...user, name: name.trim() };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      if (setUser) setUser(updatedUser);

      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.detail || "Error updating profile");
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <div className="max-w-md mx-auto px-4 py-12 text-center">
          <p className="text-slate-600">Please login to view your profile.</p>
          <Button onClick={() => navigate("/login")} className="mt-4 bg-orange-500 hover:bg-orange-600">
            Login
          </Button>
        </div>
      </div>
    );
  }

  const renderPendingPayoutsBanner = () => {
    const pendingCount = stripeStatus?.pending_payouts_count || 0;
    const pendingTotal = stripeStatus?.pending_payouts_total || 0;
    
    if (pendingCount === 0) return null;

    return (
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mb-4">
        <div className="flex items-start gap-3">
          <DollarSign className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800">Pending Payouts</h4>
            <p className="text-sm text-amber-700">
              You have <strong>{pendingCount}</strong> pending payout{pendingCount > 1 ? "s" : ""} totaling{" "}
              <strong>${pendingTotal.toFixed(2)}</strong>.
              {stripeStatus?.status === "connected" 
                ? " They are being processed automatically."
                : " Complete your Stripe setup to receive them."}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderStripeStatus = () => {
    if (!stripeStatus) {
      return (
        <div className="flex items-center text-slate-500">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Checking status...
        </div>
      );
    }

    if (stripeStatus.status === "connected") {
      return (
        <div className="space-y-3">
          <div className="flex items-center text-green-600">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span className="font-medium">Connected</span>
          </div>
          <p className="text-sm text-slate-600">
            Your Stripe account is active and ready to receive payouts.
          </p>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={handleOpenDashboard}
              disabled={stripeLoading}
              variant="outline"
              size="sm"
              data-testid="stripe-dashboard-btn"
            >
              {stripeLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <ExternalLink className="w-4 h-4 mr-2" />
              )}
              Open Stripe Dashboard
            </Button>
          </div>
        </div>
      );
    }

    if (stripeStatus.status === "pending") {
      return (
        <div className="space-y-3">
          <div className="flex items-center text-yellow-600">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span className="font-medium">Setup Incomplete</span>
          </div>
          <p className="text-sm text-slate-600">
            Your Stripe account needs additional information before you can receive payouts.
          </p>
          {stripeStatus.requirements?.currently_due?.length > 0 && (
            <p className="text-xs text-slate-500">
              Requirements: {stripeStatus.requirements.currently_due.join(", ")}
            </p>
          )}
          <Button
            onClick={handleRefreshOnboarding}
            disabled={stripeLoading}
            className="bg-orange-500 hover:bg-orange-600"
            size="sm"
            data-testid="complete-stripe-setup-btn"
          >
            {stripeLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Complete Setup
          </Button>
        </div>
      );
    }

    // Not connected
    return (
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          Connect your Stripe account to receive payouts when users purchase your uploaded items.
        </p>
        <Button
          onClick={handleConnectStripe}
          disabled={stripeLoading}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="connect-stripe-btn"
        >
          {stripeLoading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4 mr-2" />
          )}
          Connect Stripe Account
        </Button>
      </div>
    );
  };

  const renderPayoutHistory = () => {
    if (!payoutHistory || payoutHistory.payouts?.length === 0) return null;

    const { summary, payouts } = payoutHistory;

    return (
      <div className="bg-white border border-slate-200 rounded-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Payout History</h3>
          </div>
          {summary.failed_count > 0 && stripeStatus?.status === "connected" && (
            <Button
              onClick={handleRetryFailedPayouts}
              disabled={stripeLoading}
              variant="outline"
              size="sm"
            >
              {stripeLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Retry Failed ({summary.failed_count})
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {summary.completed_count > 0 && (
            <div className="bg-green-50 rounded p-3 text-center">
              <div className="text-green-600 font-semibold">${summary.completed_total.toFixed(2)}</div>
              <div className="text-xs text-green-700">{summary.completed_count} Completed</div>
            </div>
          )}
          {summary.pending_count > 0 && (
            <div className="bg-amber-50 rounded p-3 text-center">
              <div className="text-amber-600 font-semibold">${summary.pending_total.toFixed(2)}</div>
              <div className="text-xs text-amber-700">{summary.pending_count} Pending</div>
            </div>
          )}
          {summary.failed_count > 0 && (
            <div className="bg-red-50 rounded p-3 text-center">
              <div className="text-red-600 font-semibold">${summary.failed_total.toFixed(2)}</div>
              <div className="text-xs text-red-700">{summary.failed_count} Failed</div>
            </div>
          )}
        </div>

        {/* Payout List */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {payouts.slice(0, 10).map((payout) => (
            <div
              key={payout.id}
              className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded text-sm"
            >
              <div className="flex items-center gap-2">
                {payout.status === "completed" && <CheckCircle className="w-4 h-4 text-green-500" />}
                {payout.status === "pending" && <Clock className="w-4 h-4 text-amber-500" />}
                {payout.status === "failed" && <XCircle className="w-4 h-4 text-red-500" />}
                <span className="truncate max-w-[150px]" title={payout.item_title}>
                  {payout.item_title}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-medium">${payout.author_share?.toFixed(2)}</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded ${
                    payout.status === "completed"
                      ? "bg-green-100 text-green-700"
                      : payout.status === "pending"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-red-100 text-red-700"
                  }`}
                >
                  {payout.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} setUser={setUser} />

      <div className="max-w-md mx-auto px-4 py-8 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative group">
              <UserAvatar user={user} size={56} />
              <button
                type="button"
                onClick={() => document.getElementById("avatar-file-input")?.click()}
                disabled={uploadingPhoto}
                title="Change photo"
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center shadow hover:bg-slate-700 transition-colors"
                data-testid="change-photo-btn"
              >
                {uploadingPhoto ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Camera className="w-3 h-3" />
                )}
              </button>
              <input
                id="avatar-file-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoChange}
                data-testid="avatar-file-input"
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>
                My Profile
              </h1>
              <p className="text-slate-600 mt-1">Manage your account</p>
              {user?.picture?.startsWith("/api/") && (
                <button
                  type="button"
                  onClick={handlePhotoRemove}
                  className="mt-1 inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-600"
                  data-testid="remove-photo-btn"
                >
                  <Trash2 className="w-3 h-3" /> Remove photo
                </button>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(user.role === "admin" ? "/admin" : "/search")}
          >
            Go to dashboard
          </Button>
        </div>

        {fetching ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          </div>
        ) : (
          <>
            {/* Pending Payouts Banner */}
            {renderPendingPayoutsBanner()}

            {/* Profile Form */}
            <form
              onSubmit={handleSave}
              className="bg-white border border-slate-200 rounded-md p-6 space-y-6"
            >
              <h3 className="font-semibold text-slate-900">Profile Information</h3>
              <div>
                <Label htmlFor="name">Your Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="pl-10"
                    required
                    data-testid="profile-name-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  This name will be used to verify document ownership when you submit files.
                </p>
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    value={user.email}
                    disabled
                    className="pl-10 bg-slate-50"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-orange-500 hover:bg-orange-600"
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </form>

            {/* Stripe Connect Section */}
            <div className="bg-white border border-slate-200 rounded-md p-6">
              <div className="flex items-center mb-4">
                <CreditCard className="w-5 h-5 mr-2 text-slate-600" />
                <h3 className="font-semibold text-slate-900">Author Payouts</h3>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                When your paid items are purchased, you automatically receive your share of the sale.
              </p>
              {renderStripeStatus()}
            </div>

            {/* Payout History */}
            {renderPayoutHistory()}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
